import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline, Close, Search } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-quill/dist/quill.snow.css";
import Axios from "../axios";
import DateInputField from "./DateInputField";
import { useClientReactQuill } from "../utils/reactQuillLoader";

const scheduleQuillModules = {
  toolbar: {
    container: [["bold", "italic"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]],
    handlers: {
      // Quill's default link handler accepts any string, including bare
      // params/relative paths (e.g. "/race-cations") — those aren't valid
      // hrefs once this HTML is rendered on the public site, so require a
      // complete, absolute http(s) URL before applying the format.
      link(value) {
        if (!value) {
          this.quill.format("link", false);
          return;
        }
        const range = this.quill.getSelection();
        if (!range || range.length === 0) return;
        const existingHref = this.quill.getFormat(range).link || "";
        const input = window.prompt("Enter the full link URL (e.g. https://example.com/page):", existingHref);
        if (input == null) return;
        const href = input.trim();
        if (!isValidUrl(href)) {
          window.alert("Please enter a complete URL, including https://, e.g. https://example.com/page.");
          return;
        }
        this.quill.format("link", href);
      },
    },
  },
};

const blankSchedule = { time: "", time_detail: "", icon: null };
const blankItinerary = { title: "", description: "", sequence: "", images: [], videos: [], tag_ids: [], schedule: [{ ...blankSchedule }] };
const toDateInputString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const todayDateString = () => toDateInputString(new Date());
const nextDateString = (dateString) => {
  if (!dateString) return todayDateString();
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return toDateInputString(date);
};
const calculateNights = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const nights = Math.round((end - start) / (1000 * 60 * 60 * 24));
  return nights > 0 ? String(nights) : "";
};
const integerPattern = /^\d+$/;
const DEFAULT_PERCENTAGE_OF_CAUSE = "1.05";

const sanitizeRateToken = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const getRateSupplierName = (rate = {}) =>
  rate.supplierName ||
  rate.providerName ||
  rate.provider?.name ||
  rate.provider ||
  rate.supplier?.name ||
  rate.supplier ||
  "Unknown Supplier";
const buildRateOptionId = ({ planId, refundLabel, supplierName, rateId }) =>
  [planId || "plan", refundLabel || "refund", supplierName || "unknown-supplier", rateId || "rate"]
    .map((part) => sanitizeRateToken(part))
    .join("__");

const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
const ALLOWED_VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "avi", "mkv"];
const ALLOWED_IMAGE_ACCEPT = ALLOWED_IMAGE_EXTENSIONS.map((e) => `.${e}`).join(",");
const ALLOWED_VIDEO_ACCEPT = ALLOWED_VIDEO_EXTENSIONS.map((e) => `.${e}`).join(",");
const getFileExtension = (filename) => String(filename || "").split(".").pop().toLowerCase();
const validateFileExtension = (file, expectedType) => {
  const ext = getFileExtension(file.name);
  if (expectedType === "image") {
    if (!file.type.startsWith("image/") || !ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
      return `"${file.name}": unsupported image format. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")}`;
    }
    return "";
  }
  if (expectedType === "video") {
    if (!file.type.startsWith("video/") || !ALLOWED_VIDEO_EXTENSIONS.includes(ext)) {
      return `"${file.name}": unsupported video format. Allowed: ${ALLOWED_VIDEO_EXTENSIONS.join(", ")}`;
    }
    return "";
  }
  if (file.type.startsWith("image/")) {
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
      return `"${file.name}": unsupported image format. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")}`;
    }
  } else if (file.type.startsWith("video/")) {
    if (!ALLOWED_VIDEO_EXTENSIONS.includes(ext)) {
      return `"${file.name}": unsupported video format. Allowed: ${ALLOWED_VIDEO_EXTENSIONS.join(", ")}`;
    }
  } else {
    return `"${file.name}" is not a supported file type`;
  }
  return "";
};

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
const validateFileSize = (file) => {
  if (file.type.startsWith("image/") && file.size > MAX_IMAGE_SIZE) {
    return `Image "${file.name}" exceeds 2MB limit`;
  }
  if (file.type.startsWith("video/") && file.size > MAX_VIDEO_SIZE) {
    return `Video "${file.name}" exceeds 50MB limit`;
  }
  return "";
};

const isEmpty = (value) => {
  const trimmed = String(value ?? "").trim();
  // react-quill reports an empty editor as "<p><br></p>" rather than "".
  return trimmed === "" || /^<p>(<br\s*\/?>)?<\/p>$/i.test(trimmed);
};
const isValidUrl = (value) => {
  try {
    const url = new URL(String(value ?? "").trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (error) {
    return false;
  }
};
const stripInfluencerAt = (value) => String(value ?? "").replace(/@/g, "").trim();
const formatInfluencerIdForSave = (value) => {
  const handle = stripInfluencerAt(value);
  return handle ? `@${handle}` : "";
};
const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const stringifyForCompare = (value) => JSON.stringify(value ?? null);
const normalizeDealSnapshot = (value) => ({
  title: String(value.title || ""),
  hotel_id: String(value.hotel_id || ""),
  start_date: value.start_date || "",
  end_date: value.end_date || "",
  hotel_start_date: value.hotel_start_date || "",
  hotel_end_date: value.hotel_end_date || "",
  itinerary_start_date: value.itinerary_start_date || "",
  itinerary_end_date: value.itinerary_end_date || "",
  number_of_nights: calculateNights(value.hotel_start_date || value.start_date, value.hotel_end_date || value.end_date),
  itinerary_number_of_nights: calculateNights(value.itinerary_start_date, value.itinerary_end_date),
  tag_ids: Array.isArray(value.tag_ids) ? value.tag_ids.map(Number).filter(Number.isFinite) : [],
  travellers_count: String(value.travellers_count ?? ""),
  category_id: String(value.category_id || ""),

  description: value.description || "",
  trip_highlights: Array.isArray(value.trip_highlights) ? value.trip_highlights.filter(Boolean) : [],
  influencer_name: value.influencer_name || "",
  influencer_id: stripInfluencerAt(value.influencer_id),
  influencer_profile_url: value.influencer_profile_url || "",
  percentage_of_cause: String(value.percentage_of_cause || DEFAULT_PERCENTAGE_OF_CAUSE),
  image: typeof value.image === "string" ? value.image : "",
  influencer_image: typeof value.influencer_image === "string" ? value.influencer_image : "",
});
const formFontFamily = '"Poppins", sans-serif';
const dealFormSx = {
  fontFamily: formFontFamily,
  "& .deal-form-title": {
    color: "#262626",
    fontFamily: `${formFontFamily} !important`,
    fontSize: "2rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiMenuItem-root, & .MuiChip-root, & .MuiFormHelperText-root": {
    fontFamily: `${formFontFamily} !important`,
  },
  "& .MuiTypography-h6": {
    fontSize: "1.6rem",
    fontWeight: 600,
    lineHeight: 1.35,
  },
  "& .MuiTypography-subtitle1": {
    fontSize: "1.4rem",
    fontWeight: 600,
  },
  "& .MuiTypography-subtitle2": {
    fontSize: "1.3rem",
    fontWeight: 600,
  },
  "& .MuiInputBase-input, & .MuiSelect-select": {
    fontSize: "1.4rem !important",
  },
  "& .MuiInputLabel-root": {
    fontSize: "1.4rem !important",
  },
  "& .deal-form-selected-tags .MuiChip-label": {
    fontSize: "1.3rem",
    fontWeight: 500,
  },
  "& .deal-form-upload-helper .MuiFormHelperText-root": {
    color: "#6b7280 !important",
    fontSize: "1rem !important",
    fontWeight: "400 !important",
    lineHeight: 1.35,
  },
};

const DealForm = () => {
  const ReactQuill = useClientReactQuill();
  const mainImageInputRef = useRef(null);
  const influencerImageInputRef = useRef(null);
  const hotelEndDateRef = useRef(null);
  const itineraryEndDateRef = useRef(null);

  const { id } = useParams();
  const isEdit = Boolean(id);
  const location = useLocation();
  const isFromClone = Boolean(location.state?.fromClone);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagDialog, setTagDialog] = useState({ open: false, name: "", target: null, submitting: false });
  const [savedStartDate, setSavedStartDate] = useState("");
  const [deal, setDeal] = useState({
    title: "",
    hotel_id: "",
    start_date: "",
    end_date: "",
    hotel_start_date: "",
    hotel_end_date: "",
    itinerary_start_date: "",
    itinerary_end_date: "",
    number_of_nights: "",
    itinerary_number_of_nights: "",
    tag_ids: [],
    travellers_count: "",
    category_id: "",

    description: "",
    trip_highlights: [""],
    influencer_name: "",
    influencer_id: "",
    influencer_profile_url: "",
    percentage_of_cause: DEFAULT_PERCENTAGE_OF_CAUSE,
    image: null,
    influencer_image: null,
  });
  const [itineraries, setItineraries] = useState([{ ...blankItinerary, sequence: 1 }]);
  const [originalDeal, setOriginalDeal] = useState(null);
  const [originalItineraries, setOriginalItineraries] = useState([]);
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [influencerImagePreview, setInfluencerImagePreview] = useState("");

  // Hotel/Room/Rate curation
  const [numOfAdultsInput, setNumOfAdultsInput] = useState("2");
  const [numOfChildrenInput, setNumOfChildrenInput] = useState("0");
  const [hotelDetails, setHotelDetails] = useState(null);
  const [fetchingHotel, setFetchingHotel] = useState(false);
  const [roomImages, setRoomImages] = useState([]);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState("");
  const [selectedRatePlanId, setSelectedRatePlanId] = useState("");
  const [roomTypeName, setRoomTypeName] = useState("");
  const [ratePlanName, setRatePlanName] = useState("");
  const [selectedRateOptionId, setSelectedRateOptionId] = useState("");
  const [selectedSupplierName, setSelectedSupplierName] = useState("");
  // Zentrum's standardized room id + wholesaler id for the exact rate selected —
  // roomTypeId (Zentrum's recommendation groupId) alone isn't a stable enough
  // key, since the same groupId can surface a different physical room across
  // separate live searches. Saving these too keeps live pricing pinned to the
  // same room/supplier selected here.
  const [selectedStdRoomId, setSelectedStdRoomId] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [refundability, setRefundability] = useState("");
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [baseRate, setBaseRate] = useState("");
  const [taxesAndFees, setTaxesAndFees] = useState("");
  const [totalBookingAmount, setTotalBookingAmount] = useState("");

  // Discount & Margin Control
  const [publishedRate, setPublishedRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [showDiscount, setShowDiscount] = useState(true);
  // Set for exactly one pass right after an existing deal's mandatory
  // initial live-rate refresh (see fetchHotelDetails' isInitialEditLoad),
  // so that refresh's resulting totalBookingAmount change doesn't silently
  // overwrite the discount % that was actually saved to the DB — only an
  // explicit room/rate/published-rate change by the admin should recompute it.
  const suppressDiscountRecalcRef = useRef(false);

  useEffect(() => {
    let url;
    if (deal.image instanceof File) {
      url = URL.createObjectURL(deal.image);
      setMainImagePreview(url);
    } else {
      setMainImagePreview(deal.image || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [deal.image]);

  useEffect(() => {
    let url;
    if (deal.influencer_image instanceof File) {
      url = URL.createObjectURL(deal.influencer_image);
      setInfluencerImagePreview(url);
    } else {
      setInfluencerImagePreview(deal.influencer_image || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [deal.influencer_image]);

  // ── Auto-calculate Discount ────────────────────────────────────────────────
  useEffect(() => {
    if (suppressDiscountRecalcRef.current) {
      suppressDiscountRecalcRef.current = false;
      return;
    }
    const pub = parseFloat(publishedRate) || 0;
    const sevenly = parseFloat(totalBookingAmount) || 0;
    if (pub > sevenly && pub > 0) {
      const diff = pub - sevenly;
      const pct = (diff / pub) * 100;
      setDiscountAmount(parseFloat(diff.toFixed(2)));
      setDiscountPercentage(parseFloat(pct.toFixed(2)));
    } else {
      setDiscountAmount(0);
      setDiscountPercentage(0);
    }
  }, [publishedRate, totalBookingAmount]);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    // Only keep active categories/subcategories in dropdowns.
    Axios.get(`${BACKEND_URL}category`)
      .then((res) => {
        const docs = res?.data?.data?.docs || [];
        setCategories(docs.filter((c) => c?.is_active !== false));
      })
      .catch(showError);
    Axios.get(`${BACKEND_URL}tag`)
      .then((res) => {
        const docs = res?.data?.data?.docs || [];
        setTags([...docs].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      })
      .catch(showError);
  }, [BACKEND_URL]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Axios.get(`${BACKEND_URL}package/${id}`)
      .then((res) => {
        const data = res?.data?.data || {};
        const nextDeal = {
          title: data.title || "",
          hotel_id: data.hotel_id || "",
          start_date: data.start_date ? new Date(data.start_date).toISOString().split("T")[0] : "",
          end_date: data.end_date ? new Date(data.end_date).toISOString().split("T")[0] : "",
          hotel_start_date: data.hotel_start_date ? new Date(data.hotel_start_date).toISOString().split("T")[0] : (data.start_date ? new Date(data.start_date).toISOString().split("T")[0] : ""),
          hotel_end_date: data.hotel_end_date ? new Date(data.hotel_end_date).toISOString().split("T")[0] : (data.end_date ? new Date(data.end_date).toISOString().split("T")[0] : ""),
          itinerary_start_date: data.itinerary_start_date ? new Date(data.itinerary_start_date).toISOString().split("T")[0] : "",
          itinerary_end_date: data.itinerary_end_date ? new Date(data.itinerary_end_date).toISOString().split("T")[0] : "",
          number_of_nights: calculateNights(
            data.start_date ? new Date(data.start_date).toISOString().split("T")[0] : "",
            data.end_date ? new Date(data.end_date).toISOString().split("T")[0] : ""
          ) || String(data.number_of_nights ?? ""),
          itinerary_number_of_nights: calculateNights(
            data.itinerary_start_date ? new Date(data.itinerary_start_date).toISOString().split("T")[0] : "",
            data.itinerary_end_date ? new Date(data.itinerary_end_date).toISOString().split("T")[0] : ""
          ) || String(data.itinerary_number_of_nights ?? ""),
          tag_ids: Array.isArray(data.tag_ids) ? data.tag_ids.map(Number).filter(Number.isFinite) : [],
          travellers_count: String(data.travellers_count ?? ""),
          category_id: String(data.category_id?._id || data.category_id || ""),
          description: data.description || "",
          trip_highlights: Array.isArray(data.trip_highlights) && data.trip_highlights.length ? data.trip_highlights : [""],
          influencer_name: data.influencer_name || "",
          influencer_id: stripInfluencerAt(data.influencer_id),
          influencer_profile_url: data.influencer_profile_url || "",
          percentage_of_cause: isEmpty(data.percentage_of_cause) ? DEFAULT_PERCENTAGE_OF_CAUSE : String(data.percentage_of_cause),
          image: data.image || null,
          influencer_image: data.influencer_image || null,
        };
        const nextItineraries = (data.itineraries || []).map((item, idx) => ({
          title: item.title || "",
          description: item.description || "",
          sequence: item.sequence != null && item.sequence !== "" ? Number(item.sequence) : idx + 1,
          images: (item.images || []).map((url, i) => ({ file: null, url, caption: (item.image_captions || [])[i] || "" })),
          videos: (item.videos || []).map((url, i) => ({ file: null, url, caption: (item.video_captions || [])[i] || "" })),
          tag_ids: Array.isArray(item.tag_ids) ? item.tag_ids.map(Number).filter(Number.isFinite) : [],
          schedule: (item.schedule || []).length ? item.schedule : [{ ...blankSchedule }],
        }));
        setDeal(nextDeal);
        setSavedStartDate(nextDeal.start_date || "");
        setOriginalDeal(normalizeDealSnapshot(nextDeal));
        setItineraries(nextItineraries.length ? nextItineraries : [{ ...blankItinerary }]);
        setOriginalItineraries(nextItineraries);

        setNumOfAdultsInput(data.numOfAdults !== undefined ? String(data.numOfAdults) : "2");
        setNumOfChildrenInput(data.numOfChildren !== undefined ? String(data.numOfChildren) : "0");
        setSelectedRoomTypeId(data.roomTypeId || "");
        setSelectedRatePlanId(data.ratePlanId || "");
        setRoomTypeName(data.roomTypeName || "");
        setRatePlanName(data.ratePlanName || "");
        setSelectedStdRoomId(data.stdRoomId || "");
        setSelectedProviderId(data.providerId || "");
        setRefundability(data.refundability || "");
        setCancellationPolicy(data.cancellationPolicy || "");
        setBaseRate(data.baseRate !== undefined ? String(data.baseRate) : "");
        setTaxesAndFees(data.taxesAndFees !== undefined ? String(data.taxesAndFees) : "");
        setTotalBookingAmount(data.totalBookingAmount !== undefined ? String(data.totalBookingAmount) : "");
        setPublishedRate(data.publishedRate !== undefined ? data.publishedRate : 0);
        setDiscountAmount(data.discountAmount !== undefined ? data.discountAmount : 0);
        setDiscountPercentage(data.discountPercentage !== undefined ? data.discountPercentage : 0);
        setShowDiscount(data.showDiscount !== undefined ? data.showDiscount : true);
        setRoomImages(Array.isArray(data.roomImages) ? data.roomImages : []);
        const savedAdults = Math.max(1, Number(data.numOfAdults) || 2);
        const savedChildren = Math.max(0, Number(data.numOfChildren) || 0);
        if (nextDeal.hotel_id && nextDeal.hotel_start_date && nextDeal.hotel_end_date) {
          fetchHotelDetails(nextDeal.hotel_id, nextDeal.hotel_start_date, nextDeal.hotel_end_date, true, savedAdults, savedChildren);
        }
      })
      .catch(showError)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BACKEND_URL, id, isEdit]);

  const categoryOptions = useMemo(
    () => categories.filter((c) => !c.parent_id).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [categories]
  );
  const minStartDate = isEdit ? (savedStartDate || todayDateString()) : todayDateString();

  useEffect(() => {
    const hotelNights = calculateNights(deal.hotel_start_date || deal.start_date, deal.hotel_end_date || deal.end_date);
    const itineraryNights = calculateNights(deal.itinerary_start_date, deal.itinerary_end_date);
    setDeal((prev) => {
      const next = { ...prev };
      let changed = false;
      if (prev.number_of_nights !== hotelNights) { next.number_of_nights = hotelNights; changed = true; }
      if (prev.itinerary_number_of_nights !== itineraryNights) { next.itinerary_number_of_nights = itineraryNights; changed = true; }
      return changed ? next : prev;
    });
  }, [deal.start_date, deal.end_date, deal.hotel_start_date, deal.hotel_end_date, deal.itinerary_start_date, deal.itinerary_end_date]);

  const autoGenerateItineraries = (startDate, endDate) => {
    if (!startDate || !endDate) return;
    const days = Math.round((new Date(endDate) - new Date(startDate)) / 86400000);
    if (days <= 0 || days > 60) return;
    setItineraries((prev) => {
      // Only pre-fill blank placeholders; never overwrite itinerary data the user already entered.
      if (prev.some(itineraryHasContent)) return prev;
      return Array.from({ length: days }, (_, i) => {
        const dayDate = new Date(`${startDate}T00:00:00`);
        dayDate.setDate(dayDate.getDate() + i);
        const label = dayDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return { ...blankItinerary, title: `Day ${i + 1} - ${label}`, sequence: i + 1 };
      });
    });
  };

  const onDealChange = (key, value) => setDeal((prev) => ({ ...prev, [key]: value }));

  const commitHotelStartDate = (nextStart) => {
    resetHotelSelections();
    setDeal((prev) => {
      const hotelEndCleared = prev.hotel_end_date && prev.hotel_end_date <= nextStart ? "" : prev.hotel_end_date;
      const newHotelEnd = hotelEndCleared;
      const iStart = prev.itinerary_start_date && prev.itinerary_start_date < nextStart ? "" : prev.itinerary_start_date;
      const iEnd = prev.itinerary_end_date && prev.itinerary_end_date < nextStart ? "" : prev.itinerary_end_date;
      return {
        ...prev,
        hotel_start_date: nextStart,
        start_date: nextStart,
        hotel_end_date: newHotelEnd,
        end_date: newHotelEnd,
        itinerary_start_date: iStart,
        itinerary_end_date: iEnd,
      };
    });
    setTimeout(() => hotelEndDateRef.current?.open(nextStart), 0);
  };

  const commitHotelEndDate = (nextEnd) => {
    resetHotelSelections();
    setDeal((prev) => {
      const iStart = prev.itinerary_start_date && prev.itinerary_start_date > nextEnd ? "" : prev.itinerary_start_date;
      const iEnd = prev.itinerary_end_date && prev.itinerary_end_date > nextEnd ? "" : prev.itinerary_end_date;
      return { ...prev, hotel_end_date: nextEnd, end_date: nextEnd, itinerary_start_date: iStart, itinerary_end_date: iEnd };
    });
  };

  const canFetchHotelDetails = Boolean(String(deal.hotel_id || "").trim() && deal.hotel_start_date && deal.hotel_end_date);

  const resetHotelSelections = () => {
    setHotelDetails(null);
    setRoomImages([]);
    setSelectedRoomTypeId(""); setSelectedRatePlanId("");
    setRoomTypeName(""); setRatePlanName("");
    setSelectedRateOptionId(""); setSelectedSupplierName("");
    setSelectedStdRoomId(""); setSelectedProviderId("");
    setRefundability(""); setCancellationPolicy(""); setBaseRate(""); setTaxesAndFees(""); setTotalBookingAmount("");
    setPublishedRate(0); setDiscountAmount(0); setDiscountPercentage(0);
  };

  const fetchHotelDetails = async (targetId, checkIn, checkOut, isInitialEditLoad = false, overrideAdults = undefined, overrideChildren = undefined) => {
    const idToFetch = String(targetId || deal.hotel_id || "").trim();
    const ci = checkIn || deal.hotel_start_date;
    const co = checkOut || deal.hotel_end_date;
    if (!idToFetch) { toast.error("Please enter a hotel ID"); return; }
    if (!ci || !co) { toast.error("Please select hotel check-in and check-out dates first"); return; }
    const effectiveAdults = overrideAdults !== undefined ? overrideAdults : Math.max(1, Number(numOfAdultsInput || 1));
    const effectiveChildren = overrideChildren !== undefined ? overrideChildren : Math.max(0, Number(numOfChildrenInput || 0));

    if (!isInitialEditLoad) {
      if (ci < minStartDate) {
        toast.error(isEdit ? "Hotel Check-in Date cannot be before the saved Start Date" : "Hotel Check-in Date cannot be in the past");
        return;
      }
      if (co <= ci) {
        toast.error("Hotel Check-out Date must be after Check-in Date");
        return;
      }
    }

    setFetchingHotel(true);
    setHotelDetails(null);
    if (!isInitialEditLoad) {
      resetHotelSelections();
    }

    try {
      const res = await Axios.get(`${BACKEND_URL}deal/hotel/${idToFetch}/details`, {
        params: { checkIn: ci, checkOut: co, numOfAdults: effectiveAdults, numOfChildren: effectiveChildren },
      });
      if (res?.data?.success) {
        const data = res.data.data;
        if (isInitialEditLoad) suppressDiscountRecalcRef.current = true;
        setHotelDetails(data);
        setNumOfAdultsInput(String(Number(data?.numOfAdults) || effectiveAdults));
        setNumOfChildrenInput(String(Number(data?.numOfChildren) || effectiveChildren));
        if (data.roomImages?.length > 0 && !isInitialEditLoad && roomImages.length === 0) {
          setRoomImages(data.roomImages.map((r) => r.url || r));
        }
        toast.success("Hotel details loaded");
      } else {
        toast.error(res?.data?.message || "Failed to load hotel details");
      }
    } catch (err) { showError(err); }
    finally { setFetchingHotel(false); }
  };

  const allRates = hotelDetails?.roomsAndRates || [];
  const ratesForRoom = (groupId) => allRates.filter((r) => r.groupId === groupId);
  const normalizeRefundability = (rate = {}) => {
    const raw = String(rate?.refundability || "").toLowerCase();
    if (raw.includes("refund")) {
      return raw.includes("non") ? "Non-refundable" : "Refundable";
    }
    if (rate?.refundable === true) return "Refundable";
    return "Non-refundable";
  };

  const groupedRatePlansForRoom = () => {
    const candidates = selectedRoomTypeId ? ratesForRoom(selectedRoomTypeId) : allRates;
    if (candidates.length === 0) return [];
    const grouped = new Map();
    candidates.forEach((r) => {
      const planId = r.boardBasisType || r.boardBasis?.type;
      if (!planId) return;
      const refundLabel = normalizeRefundability(r);
      const supplierName = getRateSupplierName(r);
      const rateId = r.id || r.recommendationId || "";
      const total = parseFloat(r.totalBookingAmount || 0);
      if (!grouped.has(planId)) {
        grouped.set(planId, {
          planId,
          name: r.boardBasisName || r.boardBasis?.description || "Room Only",
          cheapest: new Map(), // key: refundLabel → cheapest option
        });
      }
      const current = grouped.get(planId);
      const existing = current.cheapest.get(refundLabel);
      if (!existing || total < parseFloat(existing.totalBookingAmount || 0)) {
        const optionId = buildRateOptionId({ planId, refundLabel, supplierName, rateId });
        current.cheapest.set(refundLabel, {
          optionId,
          refundLabel,
          supplierName,
          rateId,
          totalBookingAmount: r.totalBookingAmount || "0.00",
          baseRate: r.baseRate || "0.00",
          taxesAndFees: r.taxesAndFees || "0.00",
        });
      }
    });
    return Array.from(grouped.values()).map((plan) => ({
      planId: plan.planId,
      name: plan.name,
      options: Array.from(plan.cheapest.values()),
    }));
  };

  const applyRateMatch = (candidates, selectedPlan) => {
    const match =
      (selectedPlan?.rateId && candidates.find((r) => r.id === selectedPlan.rateId)) ||
      candidates.find((r) => {
        const samePlan = (r.boardBasisType || r.boardBasis?.type) === selectedPlan?.planId;
        const sameRefund = normalizeRefundability(r) === selectedPlan?.refundLabel;
        const sameSupplier = selectedPlan?.supplierName ? getRateSupplierName(r) === selectedPlan.supplierName : true;
        return samePlan && sameRefund && sameSupplier;
      }) ||
      candidates.find((r) => (r.boardBasisType || r.boardBasis?.type) === selectedPlan?.planId) ||
      candidates[0];

    if (match) {
      const supplierName = getRateSupplierName(match);
      const optionId = selectedPlan?.optionId || buildRateOptionId({
        planId: match.boardBasisType || match.boardBasis?.type,
        refundLabel: normalizeRefundability(match),
        supplierName,
        rateId: match.id || match.recommendationId,
      });

      setRefundability(normalizeRefundability(match));
      setCancellationPolicy(match.cancellationPolicy || "Cancellation policy subject to terms.");
      setBaseRate(match.baseRate || "0.00");
      setTaxesAndFees(match.taxesAndFees || "0.00");
      setTotalBookingAmount(match.totalBookingAmount || "0.00");
      setSelectedSupplierName(supplierName);
      setSelectedRateOptionId(optionId);
      setSelectedStdRoomId(match.stdRoomId || "");
      setSelectedProviderId(match.providerId || "");
    }
  };

  const handleRoomSelect = (rt, presetRatePlanId = null) => {
    setSelectedRoomTypeId(rt.id);
    setRoomTypeName(rt.name);

    const candidates = ratesForRoom(rt.id);
    if (candidates.length > 0) {
      const seen = new Set();
      const plans = candidates
        .map((r) => {
          const planId = r.boardBasisType || r.boardBasis?.type;
          const refundLabel = normalizeRefundability(r);
          return {
            id: `${planId}__${refundLabel.toLowerCase().replace(/[^a-z]/g, "")}`,
            planId,
            refundLabel,
            name: r.boardBasisName || r.boardBasis?.description || "Room Only",
            supplierName: getRateSupplierName(r),
            rateId: r.id || r.recommendationId || "",
            optionId: buildRateOptionId({
              planId,
              refundLabel,
              supplierName: getRateSupplierName(r),
              rateId: r.id || r.recommendationId,
            }),
          };
        })
        .filter((rp) => rp.planId && !seen.has(rp.id) && seen.add(rp.id));

      if (plans.length > 0) {
        const defaultNonRefundable = plans.find((p) => p.refundLabel === "Non-refundable");
        const targetPlan = presetRatePlanId
          ? (plans.find((p) => p.planId === presetRatePlanId) || defaultNonRefundable || plans[0])
          : (defaultNonRefundable || plans[0]);

        setSelectedRatePlanId(targetPlan.planId || "");
        setRatePlanName(targetPlan.name);
        setSelectedRateOptionId(targetPlan.optionId || "");
        applyRateMatch(candidates, targetPlan);
      } else {
        setSelectedRatePlanId(""); setRatePlanName("");
        setSelectedRateOptionId(""); setSelectedSupplierName("");
        setSelectedStdRoomId(""); setSelectedProviderId("");
        setRefundability(""); setCancellationPolicy(""); setBaseRate(""); setTaxesAndFees(""); setTotalBookingAmount("");
      }
    } else {
      setSelectedRatePlanId(""); setRatePlanName("");
      setSelectedRateOptionId(""); setSelectedSupplierName("");
      setSelectedStdRoomId(""); setSelectedProviderId("");
      setRefundability(""); setCancellationPolicy(""); setBaseRate(""); setTaxesAndFees(""); setTotalBookingAmount("");
    }

    if (hotelDetails?.roomImages?.length > 0) {
      const roomCodesInGroup = new Set();
      candidates.forEach((r) => { (r.occupancies || []).forEach((occ) => { if (occ.roomId) roomCodesInGroup.add(occ.roomId); }); });
      const filtered = hotelDetails.roomImages
        .filter((img) => !img.roomCodes?.length || img.roomCodes.some((rc) => roomCodesInGroup.has(rc)))
        .map((img) => img.url || img);
      setRoomImages(filtered.length > 0 ? filtered : hotelDetails.roomImages.map((img) => img.url || img));
    }
  };

  const handleRateSelect = (rp) => {
    setSelectedRatePlanId(rp.planId);
    setRatePlanName(rp.name);
    setSelectedRateOptionId(rp.optionId || "");
    setSelectedSupplierName(rp.supplierName || "");
    const candidates = selectedRoomTypeId ? ratesForRoom(selectedRoomTypeId) : allRates;
    applyRateMatch(candidates, rp);
  };

  // Auto-select first room + rate when hotel loads
  useEffect(() => {
    if (!hotelDetails) return;
    const rates = hotelDetails.roomsAndRates || [];
    if (rates.length === 0) return;

    if (isEdit && selectedRoomTypeId && selectedRatePlanId) {
      // Only stdRoomId+providerId TOGETHER is a stable, trustworthy pair —
      // verified empirically that the same numeric stdRoomId can be reused by
      // a different provider for a completely unrelated physical room, so
      // matching on stdRoomId alone (without pinning the provider too) can
      // silently select the wrong room. roomTypeId (Zentrum's recommendation
      // groupId) is even less reliable — it can surface a different physical
      // room on every fetch — so it's never used to narrow the match here.
      // Fall back to a room-name match (a stable descriptive label) when the
      // exact stdRoomId+providerId pair isn't being offered right now.
      const matchedRate = (selectedStdRoomId && selectedProviderId)
        ? rates.find((r) => r.stdRoomId === selectedStdRoomId && r.providerId === selectedProviderId)
        : null;

      const activeRoomType = matchedRate
        ? { id: matchedRate.groupId, name: matchedRate.name || roomTypeName || "Standard Room" }
        : hotelDetails.roomTypes?.find((rt) => roomTypeName && rt.name?.toLowerCase() === roomTypeName.toLowerCase());

      if (activeRoomType) {
        handleRoomSelect(activeRoomType, selectedRatePlanId);
        return;
      }
      // No reliable match — the saved room/rate isn't being offered under
      // this or any similarly-named listing right now. Fall through to the
      // same "pick the first available room" default a brand-new deal gets,
      // rather than risk a coincidental (and possibly wrong) match on the
      // unstable groupId.
    }

    const roomTypes = hotelDetails.roomTypes || [];
    if (roomTypes.length > 0) {
      handleRoomSelect(roomTypes[0]);
    } else {
      const firstRate = rates[0];
      const firstRoomType = { id: firstRate.groupId, name: firstRate.name || "Standard Room" };
      handleRoomSelect(firstRoomType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelDetails]);

  const commitItineraryStartDate = (nextStart) => {
    setDeal((prev) => ({
      ...prev,
      itinerary_start_date: nextStart,
      itinerary_end_date: prev.itinerary_end_date && prev.itinerary_end_date <= nextStart ? "" : prev.itinerary_end_date,
    }));
    setTimeout(() => itineraryEndDateRef.current?.open(nextStart), 0);
  };

  const commitItineraryEndDate = (nextEnd) => {
    setDeal((prev) => {
      if (prev.itinerary_start_date && nextEnd > prev.itinerary_start_date) {
        autoGenerateItineraries(prev.itinerary_start_date, nextEnd);
      }
      return { ...prev, itinerary_end_date: nextEnd };
    });
  };

  const onItineraryChange = (idx, key, value) => {
    setItineraries((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };

  const onScheduleChange = (itIdx, schIdx, key, value) => {
    setItineraries((prev) =>
      prev.map((it, i) =>
        i !== itIdx
          ? it
          : {
            ...it,
            schedule: it.schedule.map((s, j) => (j === schIdx ? { ...s, [key]: value } : s)),
          }
      )
    );
  };

  const removeSelectedTag = (event, values, valueToRemove, onChange) => {
    event.preventDefault();
    event.stopPropagation();
    onChange((values || []).filter((value) => Number(value) !== Number(valueToRemove)));
  };

  const normalizeTagName = (value) => String(value || "").trim().toLowerCase();

  const tagNameExists = (value) => {
    const normalized = normalizeTagName(value);
    return tags.some((t) => normalizeTagName(t.name) === normalized);
  };

  const openAddTagDialog = (target) => setTagDialog({ open: true, name: "", target, submitting: false });

  const closeAddTagDialog = () => setTagDialog({ open: false, name: "", target: null, submitting: false });

  const submitAddTag = () => {
    const trimmedName = tagDialog.name.trim();
    if (!trimmedName) {
      toast.error("Tag name is required");
      return;
    }
    if (tagNameExists(trimmedName)) {
      toast.error("Tag name already exists");
      return;
    }

    setTagDialog((prev) => ({ ...prev, submitting: true }));
    Axios.post(`${BACKEND_URL}tag`, { name: trimmedName })
      .then((res) => {
        const created = res?.data?.data;
        if (!created) return;
        setTags((prev) => [...prev, created].sort((a, b) => (a.name || "").localeCompare(b.name || "")));

        const newTagId = Number(created.tag_id);
        const target = tagDialog.target;
        if (Number.isFinite(newTagId) && target) {
          if (target.type === "deal") {
            onDealChange("tag_ids", [...(deal.tag_ids || []), newTagId]);
          } else if (target.type === "itinerary") {
            onItineraryChange(target.index, "tag_ids", [...(itineraries[target.index]?.tag_ids || []), newTagId]);
          }
        }

        toast.success(res?.data?.message || "Tag added");
        closeAddTagDialog();
      })
      .catch((err) => {
        showError(err);
        setTagDialog((prev) => ({ ...prev, submitting: false }));
      });
  };

  const setItineraryImages = (itIdx, currentImages, selectedFiles) => {
    const existing = currentImages || [];
    const validFiles = [];
    for (const f of Array.from(selectedFiles || [])) {
      const extErr = validateFileExtension(f, "image");
      if (extErr) { toast.error(extErr); continue; }
      const sizeErr = validateFileSize(f);
      if (sizeErr) { toast.error(sizeErr); continue; }
      validFiles.push({ file: f, url: null, caption: "" });
    }
    const combined = [...existing, ...validFiles];
    if (combined.length > 3) {
      toast.error("Itinerary images max 3");
    }
    onItineraryChange(itIdx, "images", combined.slice(0, 3));
  };

  const setItineraryVideos = (itIdx, currentVideos, selectedFiles) => {
    const existing = currentVideos || [];
    const validFiles = [];
    for (const f of Array.from(selectedFiles || [])) {
      const extErr = validateFileExtension(f, "video");
      if (extErr) { toast.error(extErr); continue; }
      const sizeErr = validateFileSize(f);
      if (sizeErr) { toast.error(sizeErr); continue; }
      validFiles.push({ file: f, url: null, caption: "" });
    }
    const combined = [...existing, ...validFiles];
    if (combined.length > 2) {
      toast.error("Itinerary videos max 2");
    }
    onItineraryChange(itIdx, "videos", combined.slice(0, 2));
  };

  const setMediaCaption = (itIdx, key, mediaIdx, value) => {
    setItineraries((prev) => prev.map((it, i) => (i !== itIdx ? it : {
      ...it,
      [key]: it[key].map((m, j) => (j === mediaIdx ? { ...m, caption: value } : m)),
    })));
  };

  const setScheduleIcon = (itIdx, schIdx, selectedFiles) => {
    const file = (selectedFiles || [])[0];
    if (!file) return;
    const extErr = validateFileExtension(file, "image");
    if (extErr) { toast.error(extErr); return; }
    const sizeErr = validateFileSize(file);
    if (sizeErr) { toast.error(sizeErr); return; }
    onScheduleChange(itIdx, schIdx, "icon", file);
  };

  const itineraryHasContent = (it) =>
    !isEmpty(it.title) ||
    !isEmpty(it.description) ||
    (it.tag_ids || []).length > 0 ||
    (it.images || []).length > 0 ||
    (it.videos || []).length > 0 ||
    (it.schedule || []).some((s) => !isEmpty(s.time) || !isEmpty(s.time_detail) || !!s.icon);

  const validateMediaSize = (files) => {
    for (const f of files) {
      if (f instanceof File) {
        const extErr = validateFileExtension(f);
        if (extErr) return extErr;
        const sizeErr = validateFileSize(f);
        if (sizeErr) return sizeErr;
      }
    }
    return "";
  };

  const validate = () => {
    if (isEmpty(deal.hotel_id)) {
      return "Hotel ID is required";
    }
    if (!integerPattern.test(String(deal.hotel_id).trim())) {
      return "Hotel ID must be a number only";
    }
    if (isEmpty(deal.hotel_start_date)) {
      return "Hotel Check-in Date is required";
    }
    if (isEmpty(deal.hotel_end_date)) {
      return "Hotel Check-out Date is required";
    }
    const effectiveStart = deal.hotel_start_date || deal.start_date;
    const effectiveEnd = deal.hotel_end_date || deal.end_date;
    if (!isEmpty(effectiveStart) && effectiveStart < minStartDate) {
      return isEdit
        ? "Hotel Check-in Date cannot be before the saved Start Date"
        : "Hotel Check-in Date must be today or a future date";
    }
    if (!isEmpty(effectiveStart) && !isEmpty(effectiveEnd) && effectiveEnd <= effectiveStart) {
      return "Hotel Check-out Date must be after Check-in Date";
    }
    if (!selectedRoomTypeId || !selectedRatePlanId || !totalBookingAmount) {
      return "Please fetch hotel details and select a Room Type and Rate Plan";
    }
    if (isEmpty(deal.itinerary_start_date)) {
      return "Itinerary Start Date is required";
    }
    if (isEmpty(deal.itinerary_end_date)) {
      return "Itinerary End Date is required";
    }
    if (!isEmpty(deal.itinerary_start_date) && !isEmpty(deal.itinerary_end_date) && deal.itinerary_end_date <= deal.itinerary_start_date) {
      return "Itinerary End Date must be after Itinerary Start Date";
    }
    if (isEmpty(deal.category_id)) {
      return "Category is required";
    }
    if (String(deal.title || "").length > 30) {
      return "Title must not exceed 30 characters";
    }
    if (isEmpty(deal.description)) {
      return "Description is required";
    }
    if (!isEmpty(deal.number_of_nights)) {
      const nights = toFiniteNumber(deal.number_of_nights);
      if (nights === null || nights <= 0) {
        return "Number of Nights must be greater than 0";
      }
    }
    const travellersCount = toFiniteNumber(deal.travellers_count || 0);
    if (travellersCount === null || travellersCount < 0) {
      return "Travel Cost must be 0 or greater";
    }
    const percentageOfCause = Number(deal.percentage_of_cause || 0);
    if (!Number.isFinite(percentageOfCause) || percentageOfCause < 0 || percentageOfCause > 100) {
      return "Percentage of Cause must be between 0 and 100";
    }
    if (!isEmpty(deal.influencer_profile_url) && !isValidUrl(deal.influencer_profile_url)) {
      return "Influencer Profile URL must be a valid URL";
    }
    if (deal.image instanceof File) {
      const err = validateMediaSize([deal.image]);
      if (err) return err;
    }
    if (deal.influencer_image instanceof File) {
      const err = validateMediaSize([deal.influencer_image]);
      if (err) return err;
    }
    const filledItineraries = (itineraries || []).filter(itineraryHasContent);
    if (filledItineraries.length === 0) {
      return "At least one Itinerary with content is required";
    }
    for (let i = 0; i < filledItineraries.length; i += 1) {
      const it = filledItineraries[i];
      if (isEmpty(it.title)) {
        return `Itinerary ${i + 1}: Title is required`;
      }
      if ((it.images?.length || 0) > 3) return `Itinerary ${i + 1} images max 3`;
      if ((it.videos?.length || 0) > 2) return `Itinerary ${i + 1} videos max 2`;
      const allImages = (it.images || []).map((m) => m.file);
      const err = validateMediaSize(allImages);
      if (err) return err;
      const allVideos = (it.videos || []).map((m) => m.file);
      const vidErr = validateMediaSize(allVideos);
      if (vidErr) return vidErr;
      const filledSchedule = (it.schedule || []).filter((s) => !isEmpty(s.time) || !isEmpty(s.time_detail));
      for (let j = 0; j < filledSchedule.length; j += 1) {
        if (!filledSchedule[j].time || !filledSchedule[j].time_detail) return `Itinerary ${i + 1}, schedule ${j + 1} requires time and detail`;
        const [hour = "", minute = ""] = String(filledSchedule[j].time || "").split(":");
        const hourNumber = Number(hour);
        const minuteNumber = Number(minute);
        if (!Number.isInteger(hourNumber) || hourNumber < 0 || hourNumber > 23) return `Itinerary ${i + 1}, schedule ${j + 1}: hour is invalid`;
        if (!Number.isInteger(minuteNumber) || minuteNumber < 0 || minuteNumber > 59) return `Itinerary ${i + 1}, schedule ${j + 1}: minute is invalid`;
      }
    }
    return "";
  };

  const submit = () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    const payload = new FormData();
    const appendIfCreateOrChanged = (key, value, originalValue) => {
      if (!isEdit || stringifyForCompare(value) !== stringifyForCompare(originalValue)) {
        payload.append(key, value);
      }
    };
    const serializeItineraries = (shouldAppendFiles) => itineraries.filter(itineraryHasContent).map((it, idx) => {
      const images = [];
      const imageCaptions = [];
      const uploadedImageCaptions = [];
      (it.images || []).forEach((m) => {
        const caption = m?.caption || "";
        if (m?.file instanceof File) {
          if (shouldAppendFiles) payload.append(`itinerary_images_${idx}`, m.file);
          uploadedImageCaptions.push(caption);
        } else if (m?.url) {
          images.push(m.url);
          imageCaptions.push(caption);
        }
      });
      const videos = [];
      const videoCaptions = [];
      const uploadedVideoCaptions = [];
      (it.videos || []).forEach((m) => {
        const caption = m?.caption || "";
        if (m?.file instanceof File) {
          if (shouldAppendFiles) payload.append(`itinerary_videos_${idx}`, m.file);
          uploadedVideoCaptions.push(caption);
        } else if (m?.url) {
          videos.push(m.url);
          videoCaptions.push(caption);
        }
      });
      return {
        title: it.title,
        description: it.description,
        sequence: it.sequence !== "" && it.sequence != null ? Number(it.sequence) : idx + 1,
        tag_ids: Array.isArray(it.tag_ids) ? it.tag_ids : [],
        schedule: (it.schedule || [])
          .filter((s) => !isEmpty(s.time) || !isEmpty(s.time_detail))
          .map((s, schIdx) => {
            if (s.icon instanceof File && shouldAppendFiles) payload.append(`itinerary_schedule_icon_${idx}_${schIdx}`, s.icon);
            return {
              time: s.time || "",
              time_detail: s.time_detail || "",
              icon: typeof s.icon === "string" ? s.icon : "",
            };
          }),
        images,
        videos,
        image_captions: imageCaptions,
        uploaded_image_captions: uploadedImageCaptions,
        video_captions: videoCaptions,
        uploaded_video_captions: uploadedVideoCaptions,
      };
    });
    const currentDeal = normalizeDealSnapshot(deal);
    const currentItineraries = serializeItineraries(false);
    const originalSerializedItineraries = (originalItineraries || []).filter(itineraryHasContent).map((it, idx) => ({
      title: it.title,
      description: it.description,
      sequence: it.sequence !== "" && it.sequence != null ? Number(it.sequence) : idx + 1,
      tag_ids: Array.isArray(it.tag_ids) ? it.tag_ids : [],
      schedule: (it.schedule || [])
        .filter((s) => !isEmpty(s.time) || !isEmpty(s.time_detail))
        .map((s) => ({
          time: s.time || "",
          time_detail: s.time_detail || "",
          icon: typeof s.icon === "string" ? s.icon : "",
        })),
      images: (it.images || []).filter((m) => m?.url).map((m) => m.url),
      videos: (it.videos || []).filter((m) => m?.url).map((m) => m.url),
      image_captions: (it.images || []).filter((m) => m?.url).map((m) => m.caption || ""),
      uploaded_image_captions: [],
      video_captions: (it.videos || []).filter((m) => m?.url).map((m) => m.caption || ""),
      uploaded_video_captions: [],
    }));
    const hasItineraryFiles = itineraries.some((it) =>
      [
        ...(it.images || []).map((m) => m.file),
        ...(it.videos || []).map((m) => m.file),
        ...(it.schedule || []).map((s) => s.icon),
      ].some((file) => file instanceof File)
    );

    appendIfCreateOrChanged("title", currentDeal.title, originalDeal?.title);
    appendIfCreateOrChanged("hotel_id", currentDeal.hotel_id, originalDeal?.hotel_id);
    appendIfCreateOrChanged("start_date", currentDeal.hotel_start_date || currentDeal.start_date, originalDeal?.start_date);
    appendIfCreateOrChanged("end_date", currentDeal.hotel_end_date || currentDeal.end_date, originalDeal?.end_date);
    appendIfCreateOrChanged("hotel_start_date", currentDeal.hotel_start_date, originalDeal?.hotel_start_date);
    appendIfCreateOrChanged("hotel_end_date", currentDeal.hotel_end_date, originalDeal?.hotel_end_date);

    payload.append("numOfAdults", String(Math.max(1, Number(numOfAdultsInput || 1))));
    payload.append("numOfChildren", String(Math.max(0, Number(numOfChildrenInput || 0))));
    payload.append("destination", hotelDetails?.destination || "");
    payload.append("hotel_name", hotelDetails?.hotelName || "");
    payload.append("roomTypeId", selectedRoomTypeId);
    payload.append("roomTypeName", roomTypeName);
    payload.append("stdRoomId", selectedStdRoomId);
    payload.append("providerId", selectedProviderId);
    payload.append("ratePlanId", selectedRatePlanId);
    payload.append("ratePlanName", ratePlanName);
    payload.append("refundability", refundability);
    payload.append("cancellationPolicy", cancellationPolicy);
    payload.append("baseRate", String(Number(baseRate) || 0));
    payload.append("taxesAndFees", String(Number(taxesAndFees) || 0));
    payload.append("totalBookingAmount", String(Number(totalBookingAmount) || 0));
    payload.append("publishedRate", String(Number(publishedRate) || 0));
    payload.append("discountAmount", String(Number(discountAmount) || 0));
    payload.append("discountPercentage", String(Number(discountPercentage) || 0));
    payload.append("showDiscount", String(showDiscount));
    payload.append("roomImages", JSON.stringify(roomImages));

    appendIfCreateOrChanged("itinerary_start_date", currentDeal.itinerary_start_date, originalDeal?.itinerary_start_date);
    appendIfCreateOrChanged("itinerary_end_date", currentDeal.itinerary_end_date, originalDeal?.itinerary_end_date);
    if (
      currentDeal.number_of_nights &&
      (!isEdit || currentDeal.hotel_start_date !== originalDeal?.hotel_start_date || currentDeal.hotel_end_date !== originalDeal?.hotel_end_date)
    ) {
      payload.append("number_of_nights", Number(currentDeal.number_of_nights || 0));
    }
    if (currentDeal.itinerary_number_of_nights) {
      payload.append("itinerary_number_of_nights", Number(currentDeal.itinerary_number_of_nights || 0));
    }
    appendIfCreateOrChanged("travellers_count", Number(deal.travellers_count || 0), Number(originalDeal?.travellers_count || 0));
    appendIfCreateOrChanged("category_id", currentDeal.category_id, originalDeal?.category_id);
    appendIfCreateOrChanged("description", currentDeal.description, originalDeal?.description);
    appendIfCreateOrChanged("influencer_name", currentDeal.influencer_name, originalDeal?.influencer_name);
    appendIfCreateOrChanged("influencer_id", formatInfluencerIdForSave(currentDeal.influencer_id), formatInfluencerIdForSave(originalDeal?.influencer_id));
    appendIfCreateOrChanged("influencer_profile_url", currentDeal.influencer_profile_url, originalDeal?.influencer_profile_url);
    appendIfCreateOrChanged("percentage_of_cause", Number(deal.percentage_of_cause || 0), Number(originalDeal?.percentage_of_cause || 0));
    appendIfCreateOrChanged("tag_ids", JSON.stringify(currentDeal.tag_ids), JSON.stringify(originalDeal?.tag_ids || []));
    appendIfCreateOrChanged("trip_highlights", JSON.stringify(currentDeal.trip_highlights), JSON.stringify(originalDeal?.trip_highlights || []));

    if (deal.image instanceof File) payload.append("image", deal.image);
    if (deal.influencer_image instanceof File) payload.append("influencer_image", deal.influencer_image);
    if (!isEdit || hasItineraryFiles || stringifyForCompare(currentItineraries) !== stringifyForCompare(originalSerializedItineraries)) {
      payload.append("itineraries", JSON.stringify(serializeItineraries(true)));
    }

    if (isEdit && Array.from(payload.keys()).length === 0) {
      toast.info("No changes to update");
      setLoading(false);
      return;
    }

    const request = isEdit
      ? Axios.put(`${BACKEND_URL}package/${id}`, payload, { headers: { "Content-Type": "multipart/form-data" } })
      : Axios.post(`${BACKEND_URL}package`, payload, { headers: { "Content-Type": "multipart/form-data" } });

    request
      .then((res) => {
        toast.success(res?.data?.message || "Saved successfully");
        navigate("/deals");
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  if (isEdit && loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="container-fluid" sx={dealFormSx}>
      <Helmet>
        <title>{isEdit ? "Edit Deal" : "Add Deal"}</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2 className="deal-form-title">{isEdit ? "Edit Deal" : "Add Deal"}</h2>
        <Button onClick={() => navigate("/deals")}>Back</Button>
      </div>

      <Typography variant="h6" sx={{ mb: 2 }}>Basic Info</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Hotel ID"
            required
            disabled={isEdit && !isFromClone}
            helperText={isEdit && isFromClone ? "Changing the Hotel ID will require re-selecting the room & rate for the new hotel" : undefined}
            value={deal.hotel_id}
            onChange={(e) => {
              const next = e.target.value;
              if (next === "" || integerPattern.test(next)) {
                onDealChange("hotel_id", next);
                resetHotelSelections();
              }
            }}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Hotel Booking Dates</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Check-in and check-out dates for the hotel stay</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <DateInputField
            label="Hotel Check-in Date"
            required
            value={deal.hotel_start_date}
            min={minStartDate}
            onCommit={commitHotelStartDate}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateInputField
            ref={hotelEndDateRef}
            label="Hotel Check-out Date"
            required
            value={deal.hotel_end_date}
            min={deal.hotel_start_date ? nextDateString(deal.hotel_start_date) : nextDateString(minStartDate)}
            onCommit={commitHotelEndDate}
            rangeStart={deal.hotel_start_date}
          />
        </Grid>
        <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Hotel Nights" value={deal.number_of_nights} disabled helperText="Auto-calculated from hotel dates" inputProps={{ min: 1, step: 1 }} /></Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Room & Rate Curation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Fetch live hotel data and select the room type &amp; rate plan to curate for this deal</Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6} md={2}>
          <TextField fullWidth type="number" label="Adults *" value={numOfAdultsInput}
            inputProps={{ min: 1, step: 1 }}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                setNumOfAdultsInput(val);
                resetHotelSelections();
              }
            }}
            onBlur={() => setNumOfAdultsInput(String(Math.max(1, Number(numOfAdultsInput || 1))))}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth type="number" label="Children *" value={numOfChildrenInput}
            inputProps={{ min: 0, step: 1 }}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                setNumOfChildrenInput(val);
                resetHotelSelections();
              }
            }}
            onBlur={() => setNumOfChildrenInput(String(Math.max(0, Number(numOfChildrenInput || 0))))}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button variant="contained" startIcon={fetchingHotel ? null : <Search />}
            onClick={() => fetchHotelDetails()} disabled={fetchingHotel || !canFetchHotelDetails} fullWidth sx={{ height: 56 }}>
            {fetchingHotel ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Loading…</> : "Fetch Room & Rate Details"}
          </Button>
        </Grid>
      </Grid>

      {hotelDetails && (
        <>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Hotel Details</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField fullWidth label="Hotel Name" value={hotelDetails.hotelName || ""} disabled />
                    <TextField fullWidth label="Destination" value={hotelDetails.destination || ""} disabled />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {roomImages.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Room Images
                      {selectedRoomTypeId && <Chip label={roomTypeName} size="small" sx={{ ml: 1 }} />}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {roomImages.map((url, idx) => (
                        <Box key={idx} sx={{ width: 140, height: 100, borderRadius: 2, overflow: "hidden" }}>
                          <img src={url} alt={`Room ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Available Room Types
                {selectedRoomTypeId && <Chip label="Selected" size="small" color="primary" sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a room type to filter rate plans</Typography>
              {(hotelDetails.roomTypes || []).length === 0 ? (
                <Typography variant="body2" color="warning.main">No room types found for the selected dates.</Typography>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2 }}>
                  {hotelDetails.roomTypes.map((rt, idx) => (
                    <Box key={rt.id} onClick={() => handleRoomSelect(rt)} sx={{
                      p: 2, borderRadius: 2, cursor: "pointer", transition: "all 0.2s ease",
                      border: selectedRoomTypeId === rt.id ? "2px solid #1976d2" : "1px solid #e0e0e0",
                      backgroundColor: selectedRoomTypeId === rt.id ? "#ffffff" : "#fafafa",
                      color: selectedRoomTypeId === rt.id ? "#1976d2" : "inherit",
                      "&:hover": { borderColor: "#1976d2", backgroundColor: "#ffffff" },
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{`${idx + 1}. ${rt.name}`}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Available Rate Plans
                {selectedRatePlanId && <Chip label="Selected" size="small" color="primary" sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedRoomTypeId ? "Showing rate plans for selected room type" : "Select a room type first to filter rate plans"}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 2 }}>
                {groupedRatePlansForRoom().map((plan) => (
                  <Box key={plan.planId} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{plan.name}</Typography>
                    <RadioGroup
                      value={selectedRatePlanId === plan.planId ? selectedRateOptionId : ""}
                      onChange={(e) => {
                        const option = plan.options.find((opt) => opt.optionId === e.target.value);
                        if (option) {
                          handleRateSelect({
                            planId: plan.planId,
                            name: plan.name,
                            refundLabel: option.refundLabel,
                            supplierName: option.supplierName,
                            rateId: option.rateId,
                            optionId: option.optionId,
                          });
                        }
                      }}
                    >
                      {plan.options.map((opt) => (
                        <Box key={`${plan.planId}-${opt.refundLabel}`} sx={{ border: "1px solid #e5e5e5", borderRadius: 1.5, p: 1.5, mb: 1.2, backgroundColor: "#fff" }}>
                          <FormControlLabel value={opt.optionId} control={<Radio size="small" />} label={opt.refundLabel} sx={{ m: 0 }} />
                          <Typography variant="body2" color="text.secondary">Supplier: {opt.supplierName || "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">Total: ${parseFloat(opt.totalBookingAmount || 0).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">Base: ${parseFloat(opt.baseRate || 0).toFixed(2)} | Taxes: ${parseFloat(opt.taxesAndFees || 0).toFixed(2)}</Typography>
                        </Box>
                      ))}
                    </RadioGroup>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {totalBookingAmount && (
            <Card variant="outlined" sx={{ mt: 2, borderColor: "#1976d2", borderWidth: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Booking Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Room Name" value={roomTypeName} disabled /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Rate Plan" value={ratePlanName} disabled /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Supplier" value={selectedSupplierName} disabled /></Grid>
                  <Grid item xs={6} md={3}><TextField fullWidth label="Refundability" value={refundability} disabled /></Grid>
                  <Grid item xs={6} md={3}><TextField fullWidth label="Base Rate ($)" value={baseRate} disabled /></Grid>
                  <Grid item xs={6} md={3}><TextField fullWidth label="Taxes & Fees ($)" value={taxesAndFees} disabled /></Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth label="Total Booking Amount ($)" value={totalBookingAmount} disabled
                      InputProps={{ style: { fontWeight: "bold", color: "#2e7d32" } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Cancellation Policy" value={cancellationPolicy} disabled multiline maxRows={2} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {totalBookingAmount && (
            <Card variant="outlined" sx={{ mt: 2, borderColor: "#2e7d32", borderWidth: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, color: "#2e7d32" }}>Discount &amp; Margin Control</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Published Rate ($) *"
                      type="number"
                      value={publishedRate || ""}
                      onChange={(e) => {
                        const clean = e.target.value.replace(/^0+(?=\d)/, '');
                        e.target.value = clean;
                        setPublishedRate(parseFloat(clean) || 0);
                      }}
                      placeholder="Enter compared public rate (e.g. 1000)"
                      helperText="Enter the official published rate for discount comparison."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Sevenly Total Rate ($)"
                      value={totalBookingAmount}
                      disabled
                      helperText="Auto-populated from selected room rate"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      label="Discount Amount ($)"
                      value={discountAmount}
                      disabled
                      helperText="Auto-calculated"
                      InputProps={{ style: { fontWeight: "bold" } }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      label="Discount Percentage (%)"
                      value={discountPercentage > 0 ? `${discountPercentage}%` : "0%"}
                      disabled
                      helperText="Auto-calculated"
                      InputProps={{ style: { fontWeight: "bold", color: discountPercentage > 0 ? "#2e7d32" : "inherit" } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showDiscount}
                          onChange={(e) => setShowDiscount(e.target.checked)}
                          color="success"
                        />
                      }
                      label="Display Discount Badge on Listing Page"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Itinerary Dates</Typography>
      {!(deal.hotel_start_date && deal.hotel_end_date) ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please select Hotel Booking Dates (Check-in &amp; Check-out) first.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Duration of the itinerary/tour activities — must be within hotel booking range ({deal.hotel_start_date} → {deal.hotel_end_date}).
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <DateInputField
            label="Itinerary Start Date"
            value={deal.itinerary_start_date}
            min={deal.hotel_start_date || minStartDate}
            max={deal.hotel_end_date}
            onCommit={commitItineraryStartDate}
            disabled={!(deal.hotel_start_date && deal.hotel_end_date)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateInputField
            ref={itineraryEndDateRef}
            label="Itinerary End Date"
            value={deal.itinerary_end_date}
            min={deal.itinerary_start_date ? nextDateString(deal.itinerary_start_date) : (deal.hotel_start_date || minStartDate)}
            max={deal.hotel_end_date}
            onCommit={commitItineraryEndDate}
            rangeStart={deal.itinerary_start_date}
            disabled={!(deal.hotel_start_date && deal.hotel_end_date)}
          />
        </Grid>
        <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Itinerary Days" value={deal.itinerary_number_of_nights} disabled helperText="Auto-calculated — itinerary items auto-generated" inputProps={{ min: 1, step: 1 }} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Travellers Count"
            value={deal.travellers_count}
            onChange={(e) => {
              const clean = e.target.value.replace(/^0+(?=\d)/, '');
              e.target.value = clean;
              onDealChange("travellers_count", clean);
            }}
            inputProps={{ min: 0, step: "any" }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Category</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={deal.category_id}
              onChange={(e) => setDeal((prev) => ({ ...prev, category_id: e.target.value }))}
            >
              {categoryOptions.map((c) => <MenuItem key={c._id} value={String(c._id)}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Influencer & Tags</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <Button size="small" startIcon={<Add fontSize="small" />} onClick={() => openAddTagDialog({ type: "deal" })}>
              Add New Tag
            </Button>
          </Box>
          <FormControl fullWidth>
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              label="Tags"
              value={deal.tag_ids}
              onChange={(e) =>
                onDealChange(
                  "tag_ids",
                  (typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                    .map((v) => Number(v))
                    .filter((v) => Number.isFinite(v))
                )
              }
              renderValue={(selected) => (
                <Box className="deal-form-selected-tags" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((tagId) => {
                    const tag = tags.find((t) => Number(t.tag_id || 0) === Number(tagId));
                    return (
                      <Chip
                        key={tagId}
                        label={tag?.name || `Tag ${tagId}`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDelete={(e) => {
                          removeSelectedTag(e, deal.tag_ids, tagId, (values) => onDealChange("tag_ids", values));
                        }}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {tags.map((t, idx) => (
                <MenuItem key={t._id} value={Number(t.tag_id || idx + 1)}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Percentage of Cause"
            value={deal.percentage_of_cause}
            onChange={(e) => onDealChange("percentage_of_cause", e.target.value)}
            helperText="Percentage of total donated to the cause"
            inputProps={{ min: 0, max: 100, step: "any" }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Content</Typography>
      <TextField
        fullWidth
        required
        label="Title"
        value={deal.title}
        onChange={(e) => onDealChange("title", e.target.value)}
        inputProps={{ maxLength: 30 }}
        helperText={`${(deal.title || "").length}/30`}
        sx={{ mb: 2 }}
      />
      <TextField fullWidth required multiline minRows={3} label="Description" value={deal.description} onChange={(e) => onDealChange("description", e.target.value)} />
      {(deal.trip_highlights || []).map((h, i) => (
        <Box key={`hl-${i}`} sx={{ display: "flex", gap: 1, mt: 1 }}>
          <TextField fullWidth label={`Trip Highlight ${i + 1}`} value={h} onChange={(e) => onDealChange("trip_highlights", deal.trip_highlights.map((v, idx) => (idx === i ? e.target.value : v)))} />
          <IconButton onClick={() => onDealChange("trip_highlights", deal.trip_highlights.filter((_, idx) => idx !== i))} disabled={deal.trip_highlights.length === 1}><DeleteOutline /></IconButton>
        </Box>
      ))}
      <Button startIcon={<Add />} sx={{ mt: 1 }} onClick={() => onDealChange("trip_highlights", [...deal.trip_highlights, ""])}>Add Highlight</Button>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Media</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Deal cover page</Typography>
              {mainImagePreview ? (
                <Box sx={{ mb: 1, borderRadius: 2, overflow: "hidden", border: "1px solid #ddd" }}>
                  <img src={mainImagePreview} alt="Main preview" style={{ width: "100%", maxHeight: 260, objectFit: "contain", display: "block", backgroundColor: "#f7f7f7" }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  No main image selected yet.
                </Typography>
              )}
              <TextField
                type="file"
                fullWidth
                inputRef={mainImageInputRef}
                inputProps={{ accept: ALLOWED_IMAGE_ACCEPT }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const extErr = validateFileExtension(file, "image");
                    if (extErr) {
                      toast.error(extErr);
                      if (mainImageInputRef.current) mainImageInputRef.current.value = "";
                      return;
                    }
                    const sizeErr = validateFileSize(file);
                    if (sizeErr) {
                      toast.error(sizeErr);
                      if (mainImageInputRef.current) mainImageInputRef.current.value = "";
                      return;
                    }
                  }
                  onDealChange("image", file || null);
                }}
                helperText={deal.image instanceof File ? deal.image.name : `Upload a main image for this deal (max 2MB, allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")})`}
                className="deal-form-upload-helper"
              />
              {deal.image instanceof File && (
                <Button sx={{ mt: 1 }} size="small" onClick={() => {
                  onDealChange("image", null);
                  if (mainImageInputRef.current) mainImageInputRef.current.value = "";
                }}>
                  Remove image
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Influencer Details</Typography>
              {influencerImagePreview ? (
                <Box sx={{ mb: 1, borderRadius: 2, overflow: "hidden", border: "1px solid #ddd" }}>
                  <img src={influencerImagePreview} alt="Influencer preview" style={{ width: "100%", maxHeight: 220, objectFit: "contain", display: "block", backgroundColor: "#f7f7f7" }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  No influencer image selected yet.
                </Typography>
              )}
              <TextField
                fullWidth
                label="Influencer Name"
                value={deal.influencer_name}
                onChange={(e) => onDealChange("influencer_name", e.target.value)}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                label="Influencer ID"
                value={deal.influencer_id}
                onChange={(e) => onDealChange("influencer_id", stripInfluencerAt(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "@") e.preventDefault();
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">@</InputAdornment>,
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Influencer Profile URL"
                placeholder="https://instagram.com/username"
                value={deal.influencer_profile_url}
                onChange={(e) => onDealChange("influencer_profile_url", e.target.value)}
                error={!isEmpty(deal.influencer_profile_url) && !isValidUrl(deal.influencer_profile_url)}
                helperText={
                  !isEmpty(deal.influencer_profile_url) && !isValidUrl(deal.influencer_profile_url)
                    ? "Enter a valid URL"
                    : ""
                }
                sx={{ mb: 2 }}
              />
              <TextField
                type="file"
                fullWidth
                inputRef={influencerImageInputRef}
                inputProps={{ accept: ALLOWED_IMAGE_ACCEPT }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const extErr = validateFileExtension(file, "image");
                    if (extErr) {
                      toast.error(extErr);
                      if (influencerImageInputRef.current) influencerImageInputRef.current.value = "";
                      return;
                    }
                    const sizeErr = validateFileSize(file);
                    if (sizeErr) {
                      toast.error(sizeErr);
                      if (influencerImageInputRef.current) influencerImageInputRef.current.value = "";
                      return;
                    }
                  }
                  onDealChange("influencer_image", file || null);
                }}
                helperText={deal.influencer_image instanceof File ? deal.influencer_image.name : `Upload an influencer image (max 2MB, allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")})`}
                className="deal-form-upload-helper"
              />
              {deal.influencer_image instanceof File && (
                <Button sx={{ mt: 1 }} size="small" onClick={() => {
                  onDealChange("influencer_image", null);
                  if (influencerImageInputRef.current) influencerImageInputRef.current.value = "";
                }}>
                  Remove image
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Itinerary Details</Typography>
      {itineraries.map((it, itIdx) => (
        <Box key={`it-${itIdx}`} sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1">Itinerary {itIdx + 1}</Typography>
            <IconButton onClick={() => setItineraries((prev) => prev.filter((_, idx) => idx !== itIdx))} disabled={itineraries.length === 1}><DeleteOutline /></IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Title" value={it.title} onChange={(e) => onItineraryChange(itIdx, "title", e.target.value)} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Description" value={it.description} onChange={(e) => onItineraryChange(itIdx, "description", e.target.value)} /></Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Sequence"
                helperText="Display order on the website"
                value={it.sequence ?? ""}
                onChange={(e) => onItineraryChange(itIdx, "sequence", e.target.value === "" ? "" : Number(e.target.value))}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">Tags for this Itinerary</Typography>
                <Button size="small" startIcon={<Add fontSize="small" />} onClick={() => openAddTagDialog({ type: "itinerary", index: itIdx })}>
                  Add New Tag
                </Button>
              </Box>
              <FormControl fullWidth>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  label="Tags"
                  value={it.tag_ids || []}
                  onChange={(e) =>
                    onItineraryChange(
                      itIdx,
                      "tag_ids",
                      (typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                        .map((v) => Number(v))
                        .filter((v) => Number.isFinite(v))
                    )
                  }
                  renderValue={(selected) => (
                    <Box className="deal-form-selected-tags" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((tagId) => {
                        const tag = tags.find((t) => Number(t.tag_id || 0) === Number(tagId));
                        return (
                          <Chip
                            key={tagId}
                            label={tag?.name || `Tag ${tagId}`}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDelete={(e) => {
                              removeSelectedTag(e, it.tag_ids || [], tagId, (values) => onItineraryChange(itIdx, "tag_ids", values));
                            }}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {tags.map((t, idx) => (
                    <MenuItem key={t._id} value={Number(t.tag_id || idx + 1)}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Images ({(it.images || []).length}/3)</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {(it.images || []).map((img, imgIdx) => (
                  <Box key={`img-${itIdx}-${imgIdx}`} sx={{ width: 120 }}>
                    <Box sx={{ position: "relative", width: 120, height: 120, border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                      <img src={img.url || URL.createObjectURL(img.file)} alt={`Itinerary ${itIdx + 1} - ${imgIdx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <IconButton size="small" onClick={() => onItineraryChange(itIdx, "images", it.images.filter((_, idx) => idx !== imgIdx))} sx={{ position: "absolute", top: 0, right: 0, backgroundColor: "rgba(255,255,255,0.8)" }}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Caption"
                      value={img.caption || ""}
                      onChange={(e) => setMediaCaption(itIdx, "images", imgIdx, e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}
              </Box>
              {(it.images || []).length < 3 ? (
                <TextField
                  key={`img-input-${itIdx}-${(it.images || []).length}`}
                  className="deal-form-upload-helper"
                  type="file"
                  fullWidth
                  inputProps={{ multiple: true, accept: ALLOWED_IMAGE_ACCEPT }}
                  onChange={(e) => setItineraryImages(itIdx, it.images, e.target.files)}
                  helperText={`Upload images (max 3, 2MB each — allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")})`}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Maximum 3 images reached. Remove an image above to upload a new one.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Videos ({(it.videos || []).length}/2)</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {(it.videos || []).map((vid, vidIdx) => (
                  <Box key={`vid-${itIdx}-${vidIdx}`} sx={{ width: 120 }}>
                    <Box sx={{ position: "relative", width: 120, height: 120, border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <video controls muted src={vid.url || URL.createObjectURL(vid.file)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <IconButton size="small" onClick={() => onItineraryChange(itIdx, "videos", it.videos.filter((_, idx) => idx !== vidIdx))} sx={{ position: "absolute", top: 0, right: 0, backgroundColor: "rgba(255,255,255,0.8)" }}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Caption"
                      value={vid.caption || ""}
                      onChange={(e) => setMediaCaption(itIdx, "videos", vidIdx, e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}
              </Box>
              {(it.videos || []).length < 2 ? (
                <TextField
                  key={`vid-input-${itIdx}-${(it.videos || []).length}`}
                  className="deal-form-upload-helper"
                  type="file"
                  fullWidth
                  inputProps={{ multiple: true, accept: ALLOWED_VIDEO_ACCEPT }}
                  onChange={(e) => setItineraryVideos(itIdx, it.videos, e.target.files)}
                  helperText={`Upload videos (max 2, 50MB each — allowed: ${ALLOWED_VIDEO_EXTENSIONS.join(", ")})`}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Maximum 2 videos reached. Remove a video above to upload a new one.
                </Typography>
              )}
            </Grid>
          </Grid>

          <Typography sx={{ mt: 2, mb: 1 }}>Schedule</Typography>
          {(it.schedule || []).map((sch, schIdx) => {
            const [hour = "", minute = ""] = String(sch.time || "").split(":");
            const hour24 = Number(hour);
            const hasHour = Number.isFinite(hour24) && hour !== "";
            const isPM = hasHour ? hour24 >= 12 : false;
            const displayHour = hasHour ? (hour24 > 12 ? hour24 - 12 : (hour24 === 0 ? 12 : hour24)) : "";
            const displayMinute = minute !== "" ? String(Number(minute) || 0) : "";

            const handleTimeChange = (h, m, period) => {
              const nextHour = String(h ?? "");
              const nextMinute = String(m ?? "");
              if (!/^\d{0,2}$/.test(nextHour) || !/^\d{0,2}$/.test(nextMinute)) return;
              if (!nextHour) {
                onScheduleChange(itIdx, schIdx, "time", "");
                return;
              }
              const hourNumber = Number(nextHour);
              const minuteNumber = nextMinute === "" ? 0 : Number(nextMinute);
              if (hourNumber < 1 || hourNumber > 12 || minuteNumber < 0 || minuteNumber > 59) return;
              let nextHour24 = hourNumber;
              if (period === "PM" && nextHour24 !== 12) nextHour24 += 12;
              if (period === "AM" && nextHour24 === 12) nextHour24 = 0;
              onScheduleChange(itIdx, schIdx, "time", `${String(nextHour24).padStart(2, "0")}:${String(minuteNumber).padStart(2, "0")}`);
            };

            return (
              <Grid container spacing={1} key={`sc-${itIdx}-${schIdx}`} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Hour"
                    type="text"
                    inputMode="numeric"
                    inputProps={{ maxLength: 2 }}
                    value={displayHour === "" ? "" : String(displayHour)}
                    onChange={(e) => handleTimeChange(e.target.value, displayMinute || "00", isPM ? "PM" : "AM")}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Minute"
                    type="text"
                    inputMode="numeric"
                    inputProps={{ maxLength: 2 }}
                    value={displayMinute}
                    onChange={(e) => handleTimeChange(displayHour, e.target.value, isPM ? "PM" : "AM")}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Period</InputLabel>
                    <Select
                      label="Period"
                      value={isPM ? "PM" : "AM"}
                      onChange={(e) => handleTimeChange(displayHour || "12", displayMinute || "00", e.target.value)}
                    >
                      <MenuItem value="AM">AM</MenuItem>
                      <MenuItem value="PM">PM</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <IconButton onClick={() => onItineraryChange(itIdx, "schedule", it.schedule.filter((_, idx) => idx !== schIdx))} disabled={it.schedule.length === 1} fullWidth>
                    <DeleteOutline />
                  </IconButton>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Icon
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    {sch.icon && (
                      <Box sx={{ position: "relative", width: 48, height: 48, border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", flexShrink: 0 }}>
                        <img
                          src={typeof sch.icon === "string" ? sch.icon : URL.createObjectURL(sch.icon)}
                          alt={`Schedule ${schIdx + 1} icon`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <IconButton size="small" onClick={() => onScheduleChange(itIdx, schIdx, "icon", null)} sx={{ position: "absolute", top: -4, right: -4, backgroundColor: "rgba(255,255,255,0.8)", p: 0.25 }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    <TextField
                      key={`icon-input-${itIdx}-${schIdx}-${sch.icon ? "set" : "empty"}`}
                      className="deal-form-upload-helper"
                      type="file"
                      size="small"
                      inputProps={{ accept: ALLOWED_IMAGE_ACCEPT }}
                      onChange={(e) => setScheduleIcon(itIdx, schIdx, e.target.files)}
                      helperText={`Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")}`}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Time Detail
                  </Typography>
                  {ReactQuill ? (
                    <ReactQuill
                      key={`sq-${itIdx}-${schIdx}`}
                      theme="snow"
                      value={sch.time_detail}
                      onChange={(html) => onScheduleChange(itIdx, schIdx, "time_detail", html)}
                      modules={scheduleQuillModules}
                    />
                  ) : (
                    <TextField fullWidth value={sch.time_detail} onChange={(e) => onScheduleChange(itIdx, schIdx, "time_detail", e.target.value)} />
                  )}
                </Grid>
              </Grid>
            );
          })}
          <Button startIcon={<Add />} onClick={() => onItineraryChange(itIdx, "schedule", [...it.schedule, { ...blankSchedule }])}>Add Schedule</Button>
        </Box>
      ))}
      <Button startIcon={<Add />} onClick={() => setItineraries((prev) => [...prev, { ...blankItinerary, sequence: prev.length + 1 }])}>Add Itinerary</Button>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" disabled={loading} onClick={submit}>{loading ? "Saving..." : isEdit ? "Update" : "Submit"}</Button>
        <Button onClick={() => navigate("/deals")}>Cancel</Button>
      </Box>

      <Dialog open={tagDialog.open} onClose={closeAddTagDialog} fullWidth maxWidth="sm">
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Add New Tag</h3>
          <IconButton onClick={closeAddTagDialog}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          <TextField
            label="Name"
            variant="outlined"
            required
            fullWidth
            autoFocus
            className="my-2"
            value={tagDialog.name}
            onChange={(e) => setTagDialog((prev) => ({ ...prev, name: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitAddTag();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={closeAddTagDialog}>Cancel</Button>
          <Button className="submit-button" variant="contained" disabled={tagDialog.submitting} onClick={submitAddTag}>
            {tagDialog.submitting ? "Adding..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealForm;
